import { ref, reactive, computed, watchEffect, watch } from 'vue'

export const useFetch = async (url, config = {}, json = true) => {
  const data = ref(null);
  const error = ref(null);
  const isLoading = ref(null);

  try {
    const response = await fetch(url, { ...config });

    if (!response.ok) {
      throw response.status;
      return;
    }

    data.value = json ? await response.json() : await response.text();

  } catch(err) {
    error.value = err;
  } finally {
    isLoading.value = false;
  }

  return { error, data, isLoading }
}

const cacheMap = new Map();

export const useCachedFetch = async (url, config = {}, json = true, overrideCache = false) => {
  const data = ref(null);
  const error = ref(null);
  const isLoading = ref(false);
  const isCached = ref(false);
  const timestamp = Date.now();

  const cachedResponse = () => cacheMap.get(url);

  const update = () => cacheMap.set(url, { error: error, data: data, timestamp: timestamp });
  const clear = () => cacheMap.set(url, undefined);

  if (cachedResponse() && Date.now() - cachedResponse().timestamp < 60000 && !overrideCache) {
    const repsonse = cachedResponse();
    const responseData = repsonse.data;
    const responseError = repsonse.error;

    isCached.value = true;

    await awaitCachedData(responseError, responseData)

    return { error: responseError, data: responseData, isLoading, isCached }
  }

  if ((cachedResponse() && Date.now() - cachedResponse().timestamp > 60000) || cachedResponse() && overrideCache) {
    clear();
  }

  // Adds url key and timestamp to cacheMap for subsequent requests to find
  update();

  const fetchData = await useFetch(url, config, json);

  if (fetchData.error.value) {
    clear();
  }
  
  data.value = fetchData.data.value
  error.value = fetchData.error.value;
  isLoading.value = fetchData.isLoading.value;

  // Adds data to cacheMap
  update();

  return { error, data, isLoading, clear };
}

const awaitCachedData = (error, data) => new Promise((resolve, reject) => {
   watchEffect(() => {
    if (data.value) resolve(data);
    if (error.value) reject(error)
   })
});
